<template>
  <div class="subs-page">
    <section class="subs-hero">
      <div class="label">
        <div>
          <span class="eyebrow">Marketing</span>
          <h4>Subscribers</h4>
        </div>
        <button class="btn-refresh" @click="fetchAll()">Refresh</button>
      </div>
      <p class="intro">
        Email subscribers captured via the signup form, with double opt-in consent. Statuses move
        pending → confirmed (or unsubscribed). Read-only — consent and opt-out are managed by the
        subscriber.
      </p>
    </section>

    <div v-if="error" class="alert">{{ error }}</div>

    <!-- Summary cards -->
    <div class="kpi-row">
      <div v-for="card in summaryCards" :key="card.key" class="kpi-card" :class="card.tone ? 'tone-' + card.tone : ''">
        <span class="kpi-label">{{ card.label }}</span>
        <span class="kpi-value">{{ card.value }}</span>
      </div>
    </div>

    <!-- Filters -->
    <div class="filters">
      <input
        class="hfilter grow"
        type="text"
        v-model="filters.q"
        placeholder="Search email or source"
        @input="onSearchInput"
        @keyup.enter="applyFilters"
      />
      <select class="hfilter" v-model="filters.status" @change="applyFilters">
        <option value="all">Any status</option>
        <option value="pending">Pending</option>
        <option value="confirmed">Confirmed</option>
        <option value="unsubscribed">Unsubscribed</option>
      </select>
      <a v-if="hasActiveFilters" class="focus-clear" @click="clearFilters">clear</a>
    </div>

    <!-- Table -->
    <AdminTable
      :columns="columns"
      :rows="subscribers"
      :row-key="rowKey"
      :loading="loading"
      :default-sort="{ key: 'created', dir: 'desc' }"
      empty-text="No subscribers match these filters."
    >
      <template v-slot:cell-email="{ row }">
        <span class="email-cell" :title="email(row)">{{ email(row) || '—' }}</span>
      </template>
      <template v-slot:cell-status="{ row }">
        <span class="status-pill" :class="'st-' + status(row)">{{ statusLabel(row) }}</span>
      </template>
      <template v-slot:cell-source="{ row }">
        <span class="text-cell" :title="sourceUrl(row) || source(row)">{{ source(row) || '—' }}</span>
      </template>
      <template v-slot:cell-created="{ row }">
        <span class="when-primary" :title="fmtTime(createdAt(row))">{{ fmtRelative(createdAt(row)) }}</span>
        <span class="muted-sub">{{ fmtDate(createdAt(row)) }}</span>
      </template>
      <template v-slot:cell-confirmed="{ row }">
        <span v-if="confirmedAt(row)" :title="fmtTime(confirmedAt(row))">{{ fmtDate(confirmedAt(row)) }}</span>
        <span v-else-if="unsubscribedAt(row)" class="muted-sub">unsub {{ fmtDate(unsubscribedAt(row)) }}</span>
        <span v-else class="muted-sub">—</span>
      </template>
    </AdminTable>

    <!-- Pagination -->
    <div class="pager" v-if="total > 0">
      <span class="pager-info">Showing {{ showingFrom }}–{{ showingTo }} of {{ total }}</span>
      <div class="pager-buttons">
        <button class="row-btn" :disabled="!hasPrev || loading" @click="prevPage">Previous</button>
        <button class="row-btn" :disabled="!hasNext || loading" @click="nextPage">Next</button>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios'
import { apiDomain } from '../../config'
import AdminTable from './shared/AdminTable.vue'

export default {
  name: 'Subscribers',
  components: { AdminTable },
  data () {
    return {
      summary: null,
      subscribers: [],
      total: 0,
      limit: 50,
      offset: 0,
      filters: { q: '', status: 'all' },
      loading: false,
      error: null,
      searchTimer: null
    }
  },
  computed: {
    summaryCards () {
      const s = this.summary || {}
      return [
        { key: 'total', label: 'Total', value: this.numOr(s, ['total', 'totalSubscribers', 'count']) },
        { key: 'confirmed', label: 'Confirmed', value: this.numOr(s, ['confirmed', 'confirmedCount']), tone: 'ok' },
        { key: 'pending', label: 'Pending', value: this.numOr(s, ['pending', 'pendingCount']), tone: 'warn' },
        { key: 'unsubscribed', label: 'Unsubscribed', value: this.numOr(s, ['unsubscribed', 'unsubscribedCount']) }
      ]
    },
    columns () {
      return [
        { key: 'email', label: 'Email', width: '34%', breakable: true, sortable: true, defaultDir: 'asc', sortAccessor: (r) => this.email(r) },
        { key: 'status', label: 'Status', width: '130px', nowrap: true, sortable: true, sortAccessor: (r) => this.status(r) },
        { key: 'source', label: 'Source', width: '22%', breakable: true, sortable: true, sortAccessor: (r) => this.source(r) },
        { key: 'created', label: 'Subscribed', width: '160px', nowrap: true, sortable: true, sortAccessor: (r) => new Date(this.createdAt(r)).getTime() || 0 },
        { key: 'confirmed', label: 'Confirmed', width: '150px', nowrap: true, sortable: true, sortAccessor: (r) => new Date(this.confirmedAt(r)).getTime() || 0 }
      ]
    },
    hasActiveFilters () {
      return !!(this.filters.q || this.filters.status !== 'all')
    },
    showingFrom () { return this.total === 0 ? 0 : this.offset + 1 },
    showingTo () { return Math.min(this.offset + this.limit, this.total) },
    hasPrev () { return this.offset > 0 },
    hasNext () { return this.offset + this.limit < this.total }
  },
  mounted () {
    this.fetchAll()
  },
  beforeDestroy () {
    if (this.searchTimer) clearTimeout(this.searchTimer)
  },
  methods: {
    authHeaders () {
      return {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${localStorage.usertoken}`
      }
    },
    async fetchAll () {
      await Promise.all([this.fetchSummary(), this.fetchList()])
    },
    async fetchSummary () {
      try {
        const { data } = await axios.get(`${apiDomain}admin/subscribers/summary`, { headers: this.authHeaders() })
        this.summary = this.unwrap(data, ['summary', 'data']) || data
      } catch (e) {
        this.error = this.errMsg(e)
      }
    },
    async fetchList () {
      this.loading = true
      try {
        const p = new URLSearchParams()
        p.set('status', this.filters.status || 'all')
        if (this.filters.q) p.set('q', this.filters.q.trim())
        p.set('limit', String(this.limit))
        p.set('offset', String(this.offset))
        const { data } = await axios.get(`${apiDomain}admin/subscribers?${p.toString()}`, { headers: this.authHeaders() })
        this.subscribers = this.asList(data, ['subscribers', 'items', 'data', 'results'])
        const total = this.pick(data, ['total', 'count'], null)
        this.total = total !== null ? Number(total) : this.subscribers.length
        this.error = null
      } catch (e) {
        this.error = this.errMsg(e)
      } finally {
        this.loading = false
      }
    },
    onSearchInput () {
      if (this.searchTimer) clearTimeout(this.searchTimer)
      this.searchTimer = setTimeout(() => this.applyFilters(), 400)
    },
    applyFilters () {
      if (this.searchTimer) { clearTimeout(this.searchTimer); this.searchTimer = null }
      this.offset = 0
      this.fetchList()
    },
    clearFilters () {
      this.filters = { q: '', status: 'all' }
      this.applyFilters()
    },
    prevPage () {
      if (!this.hasPrev) return
      this.offset = Math.max(0, this.offset - this.limit)
      this.fetchList()
    },
    nextPage () {
      if (!this.hasNext) return
      this.offset += this.limit
      this.fetchList()
    },
    // ---- accessors ----
    rowKey (r) { return this.pick(r, ['id', '_id', 'email'], '') },
    email (r) { return this.pick(r, ['email', 'email_address'], '') },
    status (r) {
      const s = String(this.pick(r, ['status', 'state'], '')).toLowerCase()
      if (s) return s
      if (this.unsubscribedAt(r)) return 'unsubscribed'
      if (this.confirmedAt(r)) return 'confirmed'
      return 'pending'
    },
    statusLabel (r) {
      const map = { pending: 'Pending', confirmed: 'Confirmed', unsubscribed: 'Unsubscribed' }
      const s = this.status(r)
      return map[s] || this.prettyLabel(s)
    },
    source (r) { return this.pick(r, ['source', 'signup_source'], '') },
    sourceUrl (r) { return this.pick(r, ['source_url', 'sourceUrl'], '') },
    createdAt (r) { return this.pick(r, ['created_at', 'createdAt', 'subscribed_at', 'subscribedAt'], null) },
    confirmedAt (r) { return this.pick(r, ['confirmed_at', 'confirmedAt'], null) },
    unsubscribedAt (r) { return this.pick(r, ['unsubscribed_at', 'unsubscribedAt'], null) },
    // ---- helpers ----
    pick (obj, keys, fallback) {
      if (!obj) return fallback
      for (const k of keys) {
        if (obj[k] !== undefined && obj[k] !== null && obj[k] !== '') return obj[k]
      }
      return fallback
    },
    unwrap (data, keys) {
      for (const k of keys) {
        if (data && data[k] !== undefined && data[k] !== null) return data[k]
      }
      return null
    },
    asList (data, keys) {
      if (Array.isArray(data)) return data
      const v = this.unwrap(data, keys)
      return Array.isArray(v) ? v : []
    },
    numOr (obj, keys, fallback = '—') {
      const v = this.pick(obj, keys, null)
      if (v === null) return fallback
      const n = Number(v)
      return isNaN(n) ? fallback : n
    },
    prettyLabel (value) {
      return String(value || '').replace(/[._-]+/g, ' ').trim().replace(/\b\w/g, (c) => c.toUpperCase())
    },
    fmtDate (t) {
      if (!t) return '—'
      const d = new Date(t)
      return isNaN(d) ? '—' : d.toLocaleDateString()
    },
    fmtTime (t) {
      if (!t) return '—'
      const d = new Date(t)
      return isNaN(d) ? '—' : d.toLocaleString()
    },
    fmtRelative (t) {
      if (!t) return '—'
      const d = new Date(t)
      if (isNaN(d)) return '—'
      const s = Math.round((Date.now() - d.getTime()) / 1000)
      if (s < 0) return this.fmtDate(t)
      if (s < 45) return 'just now'
      const m = Math.round(s / 60)
      if (m < 60) return `${m}m ago`
      const h = Math.round(m / 60)
      if (h < 24) return `${h}h ago`
      const days = Math.round(h / 24)
      if (days < 7) return `${days}d ago`
      return this.fmtDate(t)
    },
    errMsg (e) {
      if (e && e.response && e.response.status === 403) return 'Admin access required.'
      return (e.response && e.response.data && (e.response.data.message || e.response.data.error)) || e.message || 'Request failed'
    }
  }
}
</script>

<style scoped>
.subs-page {
  padding: 4px 8px 48px;
  color: #cfd2da;
}

.label {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  border-bottom: 1px solid rgba(67, 72, 87, 0.9);
}

.label h4 {
  margin: 0 0 6px;
  color: #d8dde8;
  font-size: 18px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.eyebrow {
  display: block;
  margin-bottom: 3px;
  color: #1997c6;
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0.1em;
  text-transform: uppercase;
}

.intro {
  max-width: 760px;
  margin: 0.6rem 0 1.1rem;
  color: #a4acbc;
  font-size: 14px;
  line-height: 1.55;
}

.alert {
  margin: 12px 0;
  padding: 10px 12px;
  border: 1px solid rgba(232, 79, 106, 0.5);
  border-radius: 8px;
  background: rgba(90, 34, 48, 0.48);
  color: #f3c6cf;
  font-size: 13px;
}

.kpi-row {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 14px;
  margin: 18px 0 8px;
}

.kpi-card {
  display: grid;
  gap: 6px;
  padding: 16px;
  border: 1px solid rgba(67, 72, 87, 0.92);
  border-radius: 8px;
  background:
    radial-gradient(circle at 12% 0%, rgba(25, 151, 198, 0.08), transparent 38%),
    rgba(14, 18, 27, 0.78);
}

.kpi-label {
  color: #8f98aa;
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.kpi-value {
  color: #eaf0f7;
  font-size: 28px;
  font-weight: 800;
  line-height: 1;
}

.kpi-card.tone-ok .kpi-value { color: #89dfbd; }
.kpi-card.tone-warn { border-color: rgba(224, 192, 126, 0.36); }
.kpi-card.tone-warn .kpi-value { color: #e0c07e; }

.filters {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  margin: 18px 0 12px;
}

.hfilter {
  height: 31px;
  border: 1px solid rgba(67, 72, 87, 0.95);
  border-radius: 6px;
  background: rgba(10, 13, 20, 0.56);
  color: #cfd2da;
  padding: 0 9px;
  font-size: 12px;
  outline: none;
}

.hfilter.grow {
  flex: 1;
  min-width: 240px;
}

.hfilter:focus {
  border-color: rgba(25, 151, 198, 0.85);
  box-shadow: 0 0 0 2px rgba(25, 151, 198, 0.12);
}

.focus-clear {
  color: #1fb2e8;
  cursor: pointer;
  font-weight: 700;
  font-size: 12px;
}

.btn-refresh {
  min-width: 84px;
  height: 31px;
  border: 1px solid rgba(25, 151, 198, 0.8);
  border-radius: 6px;
  background: rgba(25, 151, 198, 0.1);
  color: #1fb2e8;
  font-size: 12px;
  font-weight: 800;
  text-transform: uppercase;
  cursor: pointer;
}

.btn-refresh:hover {
  border-color: #1fb2e8;
  background: rgba(25, 151, 198, 0.2);
  color: #eaf7fc;
}

.email-cell {
  color: #dce1eb;
  font-weight: 600;
  word-break: break-all;
}

.muted-sub {
  display: block;
  color: #7f899b;
  font-size: 11px;
}

.when-primary { color: #cfd2da; }

.status-pill {
  display: inline-flex;
  align-items: center;
  padding: 2px 9px;
  border-radius: 999px;
  border: 1px solid rgba(117, 124, 146, 0.3);
  font-size: 10px;
  font-weight: 800;
  letter-spacing: 0.05em;
  text-transform: uppercase;
}

.status-pill.st-confirmed {
  border-color: rgba(27, 201, 142, 0.42);
  background: rgba(27, 201, 142, 0.1);
  color: #89dfbd;
}

.status-pill.st-pending {
  border-color: rgba(224, 192, 126, 0.4);
  background: rgba(224, 192, 126, 0.12);
  color: #e0c07e;
}

.status-pill.st-unsubscribed {
  background: rgba(58, 63, 77, 0.5);
  color: #9aa3b4;
}

.pager {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-top: 14px;
}

.pager-info { color: #8f98aa; font-size: 12px; }

.row-btn {
  height: 27px;
  margin-left: 8px;
  padding: 0 11px;
  border: 1px solid rgba(67, 72, 87, 0.95);
  border-radius: 6px;
  background: rgba(20, 25, 35, 0.7);
  color: #cfd2da;
  cursor: pointer;
  font-size: 11.5px;
  font-weight: 700;
}

.row-btn:hover:not(:disabled) {
  border-color: rgba(25, 151, 198, 0.7);
  color: #eaf7fc;
}

.row-btn:disabled { opacity: 0.5; cursor: default; }

@media screen and (max-width: 1100px) {
  .kpi-row { grid-template-columns: repeat(2, 1fr); }
}
</style>
